(function () {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function initReveal(scope) {
    const items = Array.from(scope.querySelectorAll?.("[data-reveal]") || []);
    const pending = items.filter((item) => item.dataset.revealReady !== "true");
    if (!pending.length) return;

    pending.forEach((item) => { item.dataset.revealReady = "true"; });

    if (reduceMotion.matches || !("IntersectionObserver" in window)) {
      pending.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, {
      root: null,
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.12
    });

    pending.forEach((item) => observer.observe(item));
  }

  function initGallery(gallery) {
    if (gallery.dataset.galleryReady === "true") return;
    gallery.dataset.galleryReady = "true";

    const viewport = gallery.querySelector("[data-horizontal-viewport]");
    const track = gallery.querySelector("[data-horizontal-rail]");
    const previous = gallery.querySelector("[data-rail-prev]");
    const next = gallery.querySelector("[data-rail-next]");
    const cards = Array.from(gallery.querySelectorAll(".collection-card"));
    const detailPanels = Array.from(gallery.querySelectorAll("[data-product-detail]"));
    const currentIndexLabel = gallery.querySelector("[data-current-index]");
    if (!viewport || !track || !cards.length) return;

    let index = 0;
    let gesture = null;
    let dragFrame = 0;
    let pendingOffset = 0;
    let suppressClickUntil = 0;

    function prepareImages() {
      cards.forEach((card) => {
        const image = card.querySelector("img");
        if (!image) return;
        image.loading = "eager";
        image.decode?.().catch(() => {});
      });
    }

    if ("IntersectionObserver" in window) {
      const preloadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        prepareImages();
        preloadObserver.disconnect();
      }, { rootMargin: "420px 0px", threshold: 0 });
      preloadObserver.observe(gallery);
    } else {
      prepareImages();
    }

    function metrics() {
      const style = getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap) || 16;
      const step = cards[0].getBoundingClientRect().width + gap;
      const maximum = Math.max(0, track.scrollWidth - viewport.clientWidth);
      const offsets = cards.map((_, cardIndex) => Math.min(maximum, cardIndex * step));
      return { maximum, offsets };
    }

    function nearestIndex(offset) {
      const { offsets } = metrics();
      let nearest = 0;
      let distance = Infinity;
      offsets.forEach((value, cardIndex) => {
        const nextDistance = Math.abs(value - offset);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = cardIndex;
        }
      });
      return nearest;
    }

    function currentOffset() {
      const transform = getComputedStyle(track).transform;
      if (!transform || transform === "none") return 0;

      if ("DOMMatrixReadOnly" in window) {
        return Math.max(0, -new DOMMatrixReadOnly(transform).m41);
      }

      const match = transform.match(/matrix(3d)?\(([^)]+)\)/);
      if (!match) return 0;
      const values = match[2].split(",").map(Number);
      const translateX = match[1] ? values[12] : values[4];
      return Math.max(0, -(translateX || 0));
    }

    function setButtonState() {
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= cards.length - 1;
    }

    function updateProductDetails() {
      cards.forEach((card, cardIndex) => {
        card.classList.toggle("is-active", cardIndex === index);
        if (cardIndex === index) {
          card.setAttribute("aria-current", "true");
        } else {
          card.removeAttribute("aria-current");
        }
      });

      detailPanels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });

      if (currentIndexLabel) {
        currentIndexLabel.textContent = String(index + 1).padStart(2, "0");
      }
    }

    function positionTrack(offset, animate) {
      track.style.transition = animate && !reduceMotion.matches
        ? "transform 620ms cubic-bezier(0.22, 1, 0.36, 1)"
        : "none";
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    function goToIndex(nextIndex, animate = true) {
      index = Math.min(cards.length - 1, Math.max(0, nextIndex));
      const { offsets } = metrics();
      gallery.classList.remove("is-interacting");
      positionTrack(offsets[index], animate);
      setButtonState();
      updateProductDetails();
    }

    previous?.addEventListener("click", () => goToIndex(index - 1));
    next?.addEventListener("click", () => goToIndex(index + 1));

    viewport.addEventListener("touchstart", (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const { maximum } = metrics();
      const offset = Math.min(maximum, currentOffset());

      positionTrack(offset, false);
      index = nearestIndex(offset);
      gesture = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        startOffset: offset,
        startIndex: index,
        startTime: performance.now(),
        direction: null
      };
    }, { passive: true });

    viewport.addEventListener("touchmove", (event) => {
      if (!gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      gesture.lastX = touch.clientX;

      if (!gesture.direction) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 10) return;
        gesture.direction = Math.abs(deltaX) > Math.abs(deltaY) * 1.2 ? "horizontal" : "vertical";
        if (gesture.direction === "horizontal") gallery.classList.add("is-interacting");
      }

      if (gesture.direction !== "horizontal") return;
      event.preventDefault();

      const { maximum } = metrics();
      pendingOffset = Math.min(maximum, Math.max(0, gesture.startOffset - deltaX));
      if (dragFrame) return;

      dragFrame = requestAnimationFrame(() => {
        positionTrack(pendingOffset, false);
        dragFrame = 0;
      });
    }, { passive: false });

    function finishGesture(cancelled) {
      if (!gesture) return;
      const completed = gesture;
      gesture = null;

      if (dragFrame) {
        cancelAnimationFrame(dragFrame);
        positionTrack(pendingOffset, false);
        dragFrame = 0;
      }

      if (cancelled && completed.direction === "horizontal") {
        goToIndex(nearestIndex(currentOffset()));
        return;
      }

      if (completed.direction !== "horizontal") {
        gallery.classList.remove("is-interacting");
        return;
      }

      const distance = completed.lastX - completed.startX;
      const elapsed = Math.max(1, performance.now() - completed.startTime);
      const velocity = distance / elapsed;
      const threshold = Math.min(56, Math.max(30, viewport.clientWidth * 0.08));
      let targetIndex = nearestIndex(currentOffset());

      if (Math.abs(distance) >= threshold || Math.abs(velocity) >= 0.35) {
        targetIndex = completed.startIndex + (distance < 0 ? 1 : -1);
      }

      if (Math.abs(distance) > 10) suppressClickUntil = performance.now() + 420;
      goToIndex(targetIndex);
    }

    viewport.addEventListener("touchend", () => finishGesture(false), { passive: true });
    viewport.addEventListener("touchcancel", () => finishGesture(true), { passive: true });
    viewport.addEventListener("click", (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    viewport.addEventListener("dragstart", (event) => event.preventDefault());
    window.addEventListener("resize", () => goToIndex(index, false), { passive: true });
    window.addEventListener("load", () => goToIndex(index, false), { once: true });

    goToIndex(0, false);
  }

  function initMaterialComparison(comparison) {
    if (comparison.dataset.comparisonReady === "true") return;
    comparison.dataset.comparisonReady = "true";

    const range = comparison.querySelector("[data-comparison-range]");
    if (!range) return;

    function update(value) {
      const clamped = Math.min(100, Math.max(0, Number(value)));
      comparison.style.setProperty("--split", `${clamped}%`);
      range.setAttribute(
        "aria-valuetext",
        `Plastic on the left ${clamped}%, recycled fiber on the right ${100 - clamped}%`
      );
    }

    range.addEventListener("input", (event) => {
      comparison.classList.remove("is-intro");
      update(event.currentTarget.value);
    });

    update(range.value);

    if (!reduceMotion.matches) {
      comparison.classList.add("is-intro");
      update(38);
      window.setTimeout(() => {
        update(50);
        range.value = "50";
      }, 220);
      window.setTimeout(() => comparison.classList.remove("is-intro"), 1200);
    }
  }

  function init(scope) {
    initReveal(scope);
    scope.querySelectorAll?.("[data-horizontal-gallery]").forEach(initGallery);
    scope.querySelectorAll?.("[data-material-comparison]").forEach(initMaterialComparison);
  }

  document.documentElement.classList.add("v6-motion-ready");
  init(document);

  if (!window.fluxynaraV8EditorEventsBound) {
    window.fluxynaraV8EditorEventsBound = true;
    document.addEventListener("shopify:section:load", (event) => init(event.target));
  }
})();
